/* global globalThis */
import { doc, getDocFromServer, runTransaction, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '@/firebaseConfig';
import { writeTrainingWithGateway } from './writeGateway';
import { TRAINING_MODULE, TRAINING_SCHEMA_VERSION, MAX_PLAN_BYTES, isValidUserId, isValidTrainingDate, serializePlan, serializePlanForWrite, validatePlan } from './model';
import { createTrainingHistoryExamples } from './historyExamples';

export const TRAINING_COLLECTION = 'allenamentiTrainer';
const TRAINER_ROLES = ['Amministratore', 'Studio'];
const now = () => globalThis.performance?.now?.() ?? Date.now();
const elapsed = start => Math.round(now() - start);

export function trainingError(code, message) {
  const error = new Error(message);
  error.code = `training/${code}`;
  return error;
}

function checkIdentity(userId) {
  if (!isValidUserId(userId)) throw trainingError('identity', 'Il riferimento al cliente non è valido.');
  const currentUser = auth.currentUser;
  if (!currentUser || !isValidUserId(currentUser.uid)) throw trainingError('unauthenticated', 'Accedi nuovamente prima di aprire gli allenamenti.');
  if (typeof navigator !== 'undefined' && navigator.onLine === false) throw trainingError('offline', 'Connessione assente. Riconnettiti e riprova.');
  return currentUser.uid;
}

function verifyTrainer(snapshot, trainerId) {
  if (auth.currentUser?.uid !== trainerId) throw trainingError('unauthenticated', 'La sessione è cambiata. Ricarica la pagina.');
  if (!snapshot.exists() || !TRAINER_ROLES.includes(snapshot.data().userRole)) {
    throw trainingError('forbidden', 'Questa funzione è riservata ai trainer abilitati.');
  }
}

export function validArchiveId(value) {
  return typeof value === 'string' && /^archive_[a-zA-Z0-9_-]{1,100}$/.test(value);
}

function validExampleKey(value) {
  return typeof value === 'string' && /^generic-history-[1-9]\d{3}-(?:0[1-9]|1[0-2])$/.test(value);
}

export function readHistory(value) {
  if (value === undefined) return [];
  if (!Array.isArray(value)) throw trainingError('corrupt', 'L’indice dello storico non è valido. Nessun dato è stato sovrascritto.');
  const seen = new Set();
  const seenExampleKeys = new Set();
  return value.map(entry => {
    if (!entry || typeof entry !== 'object' || !validArchiveId(entry.id) || seen.has(entry.id) ||
      ['periodName', 'title', 'startDate', 'endDate'].some(field => typeof entry[field] !== 'string') ||
      !entry.periodName.trim() || entry.periodName.length > 160 || !entry.title.trim() || entry.title.length > 160 ||
      ['startDate', 'endDate'].some(field => entry[field] !== '' && !isValidTrainingDate(entry[field])) ||
      (entry.startDate && entry.endDate && entry.endDate < entry.startDate) ||
      !Number.isInteger(entry.workoutCount) || entry.workoutCount < 1 || entry.workoutCount > 24 ||
      !Number.isInteger(entry.exerciseCount) || entry.exerciseCount < 0 || entry.exerciseCount > 500 ||
      (entry.archivedAt !== undefined && (typeof entry.archivedAt !== 'string' || !Number.isFinite(Date.parse(entry.archivedAt)))) ||
      (entry.exampleKey !== undefined && (!validExampleKey(entry.exampleKey) || seenExampleKeys.has(entry.exampleKey)))) {
      throw trainingError('corrupt', 'L’indice dello storico contiene dati non validi. Nessun dato è stato sovrascritto.');
    }
    seen.add(entry.id);
    const summary = Object.fromEntries(['id', 'periodName', 'title', 'startDate', 'endDate', 'workoutCount', 'exerciseCount'].map(field => [field, entry[field]]));
    if (entry.archivedAt !== undefined) summary.archivedAt = entry.archivedAt;
    if (entry.exampleKey !== undefined) { summary.exampleKey = entry.exampleKey; seenExampleKeys.add(entry.exampleKey); }
    return summary;
  });
}

export function assertDocumentSize(payload) {
  if (new TextEncoder().encode(JSON.stringify(payload)).length > MAX_PLAN_BYTES) {
    throw trainingError('size', 'Il programma e il suo indice storico superano il limite del documento. Riduci il contenuto prima di salvare; nessun dato è stato modificato.');
  }
}

export function readRecord(snapshot, userId, kind = 'current') {
  if (!snapshot.exists()) return null;
  const data = snapshot.data();
  if (data.module !== TRAINING_MODULE || data.schemaVersion !== TRAINING_SCHEMA_VERSION || data.userId !== userId ||
    (kind === 'archive' ? data.kind !== 'archive' : data.kind !== undefined && !['current', 'empty'].includes(data.kind))) {
    throw trainingError('incompatible', 'Esiste già un documento con un formato diverso. Nessun dato è stato sovrascritto.');
  }
  if (kind === 'current' && data.kind === 'empty') {
    const allowed = ['module', 'schemaVersion', 'kind', 'userId', 'history', 'revision', 'createdAt', 'createdBy', 'updatedAt', 'updatedBy'];
    if (!Number.isSafeInteger(data.revision) || data.revision < 1 || Object.keys(data).some(key => !allowed.includes(key))) {
      throw trainingError('corrupt', 'Lo storico senza allenamento attuale contiene dati non validi. Nessun dato è stato sovrascritto.');
    }
    const history = readHistory(data.history);
    assertDocumentSize(data);
    return { plan: null, revision: data.revision, history, emptyCurrent: true, updatedAt: data.updatedAt || null };
  }
  if (!Number.isSafeInteger(data.revision) || data.revision < 1 || validatePlan(data).length ||
    (data.exampleKey !== undefined && !validExampleKey(data.exampleKey))) {
    throw trainingError('corrupt', 'Il programma salvato contiene dati non validi. Nessun dato è stato sovrascritto.');
  }
  const history = kind === 'current' ? readHistory(data.history) : [];
  const plan = {
    ...serializePlan(data),
    kind,
    revision: data.revision,
    createdAt: data.createdAt || null,
    createdBy: typeof data.createdBy === 'string' ? data.createdBy : '',
    updatedAt: data.updatedAt || null,
    updatedBy: typeof data.updatedBy === 'string' ? data.updatedBy : '',
  };
  assertDocumentSize({ ...plan, history });
  return { plan, revision: data.revision, history, emptyCurrent: false, updatedAt: data.updatedAt || null, archivedAt: data.archivedAt || null };
}

function timestampDate(value) {
  if (value && typeof value.toDate === 'function') return value.toDate();
  return value instanceof Date ? value : null;
}

export async function loadTraining(userId) {
  const start = now();
  const trainerId = checkIdentity(userId);
  const trainer = await getDocFromServer(doc(db, 'users', trainerId));
  verifyTrainer(trainer, trainerId);
  // Server reads prevent an offline/stale cache from being mistaken for an empty plan.
  const snapshot = await getDocFromServer(doc(db, TRAINING_COLLECTION, userId));
  if (auth.currentUser?.uid !== trainerId) throw trainingError('unauthenticated', 'La sessione è cambiata. Ricarica la pagina.');
  const record = readRecord(snapshot, userId);
  const plan = record?.plan || null;
  return { plan, revision: record?.revision || 0, savedAt: timestampDate(record?.updatedAt), elapsedMs: elapsed(start),
    history: record?.history || [], emptyCurrent: !plan };
}

export async function loadTrainingHistory(userId, archiveId) {
  const start = now();
  const trainerId = checkIdentity(userId);
  if (!validArchiveId(archiveId) || archiveId === userId) throw trainingError('identity', 'Il riferimento all’allenamento storico non è valido.');
  const trainer = await getDocFromServer(doc(db, 'users', trainerId));
  verifyTrainer(trainer, trainerId);
  const snapshot = await getDocFromServer(doc(db, TRAINING_COLLECTION, archiveId));
  if (auth.currentUser?.uid !== trainerId) throw trainingError('unauthenticated', 'La sessione è cambiata. Ricarica la pagina.');
  const record = readRecord(snapshot, userId, 'archive');
  if (!record) throw trainingError('not-found', 'L’allenamento storico richiesto non è disponibile.');
  return { plan: record.plan, revision: record.plan.revision, savedAt: timestampDate(record.plan.updatedAt), elapsedMs: elapsed(start), archiveId, archivedAt: timestampDate(record.archivedAt) };
}

export function newArchiveId() {
  return `archive_${globalThis.crypto?.randomUUID?.() || `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`}`;
}

async function commitTraining(userId, plan, expectedRevision, newPeriod) {
  const start = now();
  const trainerId = checkIdentity(userId);
  if (plan?.userId !== userId) throw trainingError('identity', 'Il programma appartiene a un altro cliente. Ricarica la pagina.');
  if (plan.kind === 'archive') throw trainingError('archive-readonly', 'Gli allenamenti nello storico sono in sola lettura. Crea una nuova copia per modificarli.');
  if (!Number.isSafeInteger(expectedRevision) || expectedRevision < 0 || expectedRevision >= Number.MAX_SAFE_INTEGER) throw trainingError('conflict', 'La versione del programma non è valida. Ricarica la scheda.');
  // Snapshot the payload before asynchronous work; edits made during a save stay local.
  const payload = serializePlanForWrite(plan);
  const remote = await writeTrainingWithGateway(newPeriod ? 'save-new' : 'save', { userId, plan: payload, expectedRevision });
  if (remote) return { ...remote, elapsedMs: elapsed(start) };
  const target = doc(db, TRAINING_COLLECTION, userId);
  const archiveId = newPeriod ? newArchiveId() : null;
  const archivedAtIso = new Date().toISOString();
  const result = await runTransaction(db, async transaction => {
    // Keep independent reads in the transaction so commit still verifies both versions.
    const [trainer, snapshot] = await Promise.all([
      transaction.get(doc(db, 'users', trainerId)),
      transaction.get(target),
    ]);
    verifyTrainer(trainer, trainerId);
    const record = readRecord(snapshot, userId);
    const existing = record?.plan || null;
    if ((record?.revision || 0) !== expectedRevision) {
      throw trainingError('conflict', 'Questo programma è stato modificato in un’altra finestra o da un altro trainer. Le tue modifiche restano nella pagina: confrontale prima di ricaricare.');
    }
    let history = record?.history || [];
    let archiveTarget;
    let archivePayload;
    if (newPeriod && existing) {
      archiveTarget = doc(db, TRAINING_COLLECTION, archiveId);
      const archiveSnapshot = await transaction.get(archiveTarget);
      if (archiveSnapshot.exists()) throw trainingError('archive-collision', 'Il riferimento dello storico è già occupato. Riprova: nessun dato è stato sovrascritto.');
      archivePayload = { ...existing, kind: 'archive', archivedAt: serverTimestamp(), archivedBy: trainerId };
      assertDocumentSize(archivePayload);
      history = [{
        id: archiveId,
        periodName: existing.periodName,
        title: existing.title,
        startDate: existing.startDate,
        endDate: existing.endDate,
        workoutCount: existing.workouts.length,
        exerciseCount: existing.workouts.reduce((total, workout) => total + workout.exercises.length, 0),
        archivedAt: archivedAtIso,
      }, ...history];
    }
    if (auth.currentUser?.uid !== trainerId) throw trainingError('unauthenticated', 'La sessione è cambiata. Ricarica la pagina.');
    const nextRevision = expectedRevision + 1;
    const currentPayload = {
      ...payload,
      kind: 'current',
      history,
      revision: nextRevision,
      createdAt: (!newPeriod && existing?.createdAt) || serverTimestamp(),
      createdBy: (!newPeriod && existing?.createdBy) || trainerId,
      updatedAt: serverTimestamp(),
      updatedBy: trainerId,
    };
    assertDocumentSize(currentPayload);
    // All reads and validation precede both writes; a failed commit archives nothing.
    if (archiveTarget) transaction.set(archiveTarget, archivePayload);
    transaction.set(target, currentPayload);
    return { revision: nextRevision, history };
  });
  // This is the acknowledgement time. Database timestamps are always generated by Firestore.
  // Do not add a post-write read or timeout that could report a committed write as failed.
  return { ...result, savedAt: new Date(), elapsedMs: elapsed(start) };
}

export function saveTraining(userId, plan, expectedRevision) {
  return commitTraining(userId, plan, expectedRevision, false);
}

export function saveNewTraining(userId, plan, expectedRevision) {
  return commitTraining(userId, plan, expectedRevision, true);
}

export async function addTrainingHistoryExamples(userId, expectedRevision) {
  const start = now();
  const trainerId = checkIdentity(userId);
  if (!Number.isSafeInteger(expectedRevision) || expectedRevision < 1 || expectedRevision >= Number.MAX_SAFE_INTEGER) throw trainingError('conflict', 'Salva prima l’allenamento corrente e ricarica la scheda prima di aggiungere gli esempi.');
  const remote = await writeTrainingWithGateway('history-examples', { userId, expectedRevision });
  if (remote) return { ...remote, elapsedMs: elapsed(start) };
  const target = doc(db, TRAINING_COLLECTION, userId);
  const archiveIds = [newArchiveId(), newArchiveId()];
  const referenceDate = new Date();
  const archivedAtIso = referenceDate.toISOString();
  const result = await runTransaction(db, async transaction => {
    const trainer = await transaction.get(doc(db, 'users', trainerId));
    verifyTrainer(trainer, trainerId);
    const snapshot = await transaction.get(target);
    const record = readRecord(snapshot, userId);
    if (!record?.plan) throw trainingError('not-found', 'Salva l’allenamento corrente prima di aggiungere gli esempi allo storico.');
    if (record.plan.revision !== expectedRevision) throw trainingError('conflict', 'L’allenamento corrente è stato modificato in un’altra finestra. Ricarica prima di aggiungere gli esempi allo storico.');
    const examples = createTrainingHistoryExamples(record.plan, userId, referenceDate);
    const existingKeys = new Set(record.history.map(entry => entry.exampleKey).filter(Boolean));
    const missing = examples.map((example, index) => ({ ...example, archiveId: archiveIds[index] })).filter(example => !existingKeys.has(example.exampleKey));
    if (!missing.length) {
      if (auth.currentUser?.uid !== trainerId) throw trainingError('unauthenticated', 'La sessione è cambiata. Ricarica la pagina.');
      return { revision: record.plan.revision, history: record.history, savedAt: timestampDate(record.plan.updatedAt), createdCount: 0 };
    }
    if (new Set(missing.map(example => example.archiveId)).size !== missing.length) throw trainingError('archive-collision', 'I riferimenti dello storico coincidono. Riprova: nessun dato è stato modificato.');
    const prepared = [];
    for (const example of missing) {
      const archiveTarget = doc(db, TRAINING_COLLECTION, example.archiveId);
      const archiveSnapshot = await transaction.get(archiveTarget);
      if (archiveSnapshot.exists()) throw trainingError('archive-collision', 'Il riferimento dello storico è già occupato. Riprova: nessun dato è stato sovrascritto.');
      const payload = {
        ...serializePlan(example.plan),
        kind: 'archive',
        exampleKey: example.exampleKey,
        revision: 1,
        createdAt: serverTimestamp(),
        createdBy: trainerId,
        updatedAt: serverTimestamp(),
        updatedBy: trainerId,
        archivedAt: serverTimestamp(),
        archivedBy: trainerId,
      };
      assertDocumentSize(payload);
      prepared.push({ target: archiveTarget, payload, summary: {
        id: example.archiveId,
        exampleKey: example.exampleKey,
        periodName: example.plan.periodName,
        title: example.plan.title,
        startDate: example.plan.startDate,
        endDate: example.plan.endDate,
        workoutCount: example.plan.workouts.length,
        exerciseCount: example.plan.workouts.reduce((total, workout) => total + workout.exercises.length, 0),
        archivedAt: archivedAtIso,
      } });
    }
    const history = [...record.history, ...prepared.map(example => example.summary)].sort((first, second) => second.startDate.localeCompare(first.startDate));
    const revision = record.plan.revision + 1;
    // Retain the exact saved current program and creation metadata; only its index changes.
    const currentPayload = { ...snapshot.data(), kind: 'current', history, revision, updatedAt: serverTimestamp(), updatedBy: trainerId };
    assertDocumentSize(currentPayload);
    if (auth.currentUser?.uid !== trainerId) throw trainingError('unauthenticated', 'La sessione è cambiata. Ricarica la pagina.');
    prepared.forEach(example => transaction.set(example.target, example.payload));
    transaction.set(target, currentPayload);
    return { revision, history, createdCount: prepared.length };
  });
  return { ...result, savedAt: result.createdCount ? new Date() : result.savedAt, elapsedMs: elapsed(start) };
}

export function getTrainingErrorMessage(error) {
  if (error?.code?.startsWith('training/')) return error.message;
  const code = (error?.code || '').replace(/^firestore\//, '');
  const messages = {
    'permission-denied': 'Firebase non autorizza questa operazione. Le regole della collection allenamentiTrainer devono consentire l’accesso ai trainer. Nessuna modifica locale è stata persa.',
    unauthenticated: 'La sessione è scaduta. Accedi nuovamente e riprova.',
    unavailable: 'Firebase non è raggiungibile. Verifica la connessione e riprova; le modifiche restano nella pagina.',
    'deadline-exceeded': 'Firebase non ha confermato l’operazione in tempo. Le modifiche restano nella pagina: verifica lo stato prima di ricaricare.',
    aborted: 'Il programma sta ricevendo altre modifiche. Riprova il salvataggio; le modifiche restano nella pagina.',
    'resource-exhausted': 'Firebase ha raggiunto un limite temporaneo. Riprova più tardi; le modifiche restano nella pagina.',
  };
  return messages[code] || 'Operazione non completata. Le modifiche restano nella pagina; riprova o verifica la connessione.';
}
